import React, { useState, useEffect, useRef, useCallback } from "react";
import LeadDbService from "../../../Services/LeadDbService";
import EstimateService from "../../../Services/EstimateService";
import CustomStepProgress from "../../../common/CustomStepProgress";
import EstimateDownloadPDF from "../../PropertiesComponents/EstimateSection/EstimateDownloadPdf";
import BasicInfoStep from "./EstimateSteps/BasicInfoStep";
import PropertyInfoStep from "./EstimateSteps/PropertyInfoStep";
import ChargesStep from "./EstimateSteps/ChargesStep";
import { userUid } from "../../../../utils/storage";
import jsPDF from "jspdf";
import { toast } from "react-toastify";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../../Constant/Notification";

import loadingGIF from "../../../../assets/LoadingGIF/PropertyCertificate-ezgif.com-optimize.gif";

const EstimateGeneration = ({
  isOpen,
  onClose,
  propertyId,
  selectedPropertyName,
  selectedPropertyType,
}) => {
  const steps = [
    { id: 1, label: "Basic Info" },
    { id: 2, label: "Property Info" },
    { id: 3, label: "Charges" },
  ];

  const pdfRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [unitNumberOptions, setUnitNumberOptions] = useState([]);
  const [towerOptions, setTowerOptions] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [leadOptions, setLeadOptions] = useState([]);
  const [searchLead, setSearchLead] = useState("");
  const [unitInfo, setUnitInfo] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const [formData, setFormData] = useState({
    selectedUnit: null,
    selectedLead: null,
    selectedPercentage: "",
    selectedTower: null,
    selectedFurnished: null,
    parkingOptions: {
      fullyCovered: 0,
      semiCovered: 0,
      open: 0,
    },
  });

  const fetchPlottedData = useCallback(async () => {
    if (!isOpen) return;
    try {
      setLoading(true);
      const response = await EstimateService.getPropertyUnits(
        propertyId,
        selectedTower?.value
      );
      const propertyData = response?.data || [];
      setUnitNumberOptions(propertyData);

      if (propertyData.length === 1) {
        setFormData((prev) => ({
          ...prev,
          selectedUnit: {
            value: propertyData[0].id,
            label: propertyData[0].name,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching property data:", error);
      toast.error("Failed to load property units");
    } finally {
      setLoading(false);
    }
  }, [isOpen, propertyId, selectedTower?.value]);

  const fetchLead = async () => {
    if (!isOpen) return;
    try {
      setLoading(true);
      const body = { searchKey: searchLead };
      const response = await EstimateService.getEstimateLead(body);

      setLeadOptions(response?.data?.leads || []);
    } catch (error) {
      console.error(error);
      setLeadOptions([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchLead();
    }, 1000);
    return () => clearTimeout(delayDebounce);
  }, [searchLead, isOpen]);

  const fetchTowerList = useCallback(async () => {
    try {
      setLoading(true);
      const response = await EstimateService.getTowerProperty(propertyId);
      setTowerOptions(response?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    if (isOpen) {
      fetchPlottedData();
      if (selectedPropertyType === "Apartment") {
        fetchTowerList();
      }
    }
  }, [isOpen, fetchPlottedData, fetchTowerList, selectedPropertyType]);

  const handleClose = () => {
    setFormData({
      selectedUnit: null,
      selectedLead: null,
      selectedPercentage: "",
      selectedTower: null,
      selectedFurnished: null,

      parkingOptions: {
        fullyCovered: 0,
        semiCovered: 0,
        open: 0,
      },
    });
    setGeneratingPDF(false)
    setSearchLead();
    setTowerOptions([]);
    setLeadOptions([]);
    setSelectedTower("");
    setCurrentStep(1);
    setUnitInfo({});
    setValidationErrors({});
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleParkingChange = (type, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);

    if(numValue > 5){
      showInfoToast("Maximum parking allowed is 5")
      return;
    }

    setFormData((prev) => ({
      ...prev,
      parkingOptions: {
        ...prev.parkingOptions,
        [type]: numValue,
      },
    }));
  };

  const handleUnitInfoChange = (field, value) => {
    setUnitInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBasicInfoSubmit = async () => {
    if (!formData.selectedUnit?.value) {
      showErrorToast("Please select a unit");
      return;
    }

    if (!formData.selectedPercentage) {
      showErrorToast(" Please  Enter a percentage ");
      return;
    }
    if (!formData.selectedLead?.id) {
      showErrorToast(" Please select a lead option ");
      return;
    }

    setLoading(true);
    try {
      const body = {
        propertyId,
        propertyType: selectedPropertyType,
        unitId: formData.selectedUnit?.value,
      };
      const response = await EstimateService.getPropertyPopulatedData(body);

      if (response.data) {
        setUnitInfo((prev) => ({
          ...prev,
          ...response.data,
          ratePerSqft: response.data.ratePerSqft || 0,
          baseRate: response.data.baseRate || 0,
          preferredLocationCharge: response.data.preferredLocationCharge || 0,
          gst: response.data.gst || 0,
          registrationCharge: response.data.registrationCharge || 0,
          documentCharge: response.data.documentCharge || 0,
          electricityCharge: response.data.electricityCharge || 0,
          furnishedCharge: response.data.furnishedCharge || 0,
          surveyFee: response.data.surveyFee || 0,
        }));

        handleInputChange("selectedFurnished", response.data?.furnished || "");
        setCurrentStep(2);
      }
    } catch (error) {
      console.error("Error fetching property data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateEstimate = () => {
    if (selectedPropertyType !== "Layout") {
      if (!formData.selectedFurnished) {
        showErrorToast("Please select  a furnished option");
        return;
      }
    }
    setCurrentStep(3);
  };

  const handleDownloadEstimate = async () => {

    setLoading(true);
    setGeneratingPDF(true)
    if (!selectedPropertyType || !unitInfo) {
      showErrorToast("Please complete all required fields");
      return;
    }

    if (selectedPropertyType === "Layout") {
      if (!unitInfo?.ratePerSqft) {
        showErrorToast("Please select rate per sqft");
        return;
      }
    }

    if (selectedPropertyType === "Apartment") {
      if (!unitInfo?.baseRate) {
        showErrorToast("Please select base rate");
        return;
      }
      

      if (formData.selectedFurnished !== "Unfurnished") {
        if (!unitInfo?.furnishedCharge) {
          showErrorToast("Please select furnished charge");
          return;
        }
      }
    }

    if (selectedPropertyType === "Villa") {
      if (!unitInfo?.baseRate) {
        showErrorToast("Please select base rate");
        return;
      }

      if (formData.selectedFurnished !== "Unfurnished") {
        if (!unitInfo?.furnishedCharge) {
          showErrorToast("Please select furnished charge");
          return;
        }
      }
    }

   
    try {
      const estimateData = buildEstimateData();
      const payload = {
        propertyType: selectedPropertyType,
        leadId: formData.selectedLead?.id || "",
        estimateData,
      };
    
      const config = {
        responseType: 'blob',
      };
    

      if (payload) {
        console.log(payload);
      }

  

      const response = await EstimateService.estimateCreating(payload, config);

      // if (response.status === 200) {
      //   if (pdfRef.current && response.data) {
      //     setGeneratingPDF(true);

      //     await pdfRef.current.generatePDF(response.data);
      //     showSuccessToast("Estimate downloaded successfully");
      //     handleClose();
      //   } else {
      //     console.error("PDF ref or HTML content not available");
      //   }
      // }

     
     
      if(response.status === 200)
      {

      
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
    
        // 👇 Use filename from response headers
        const disposition = response.headers['content-disposition'];
        const match = disposition && disposition.match(/filename="?(.+)"?/);
        const filename = match ? match[1] : 'estimate.pdf';
    
        link.setAttribute('download', filename);
        showSuccessToast("Estimate downloaded successfully");
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        window.URL.revokeObjectURL(url);
      }
     
    
    } catch (error) {
      console.error("Error generating estimate:", error);
    } finally {
      setLoading(false);
      setGeneratingPDF(false);
    }
  };

  console.log("Tower Form", selectedTower);
  const buildEstimateData = () => {
    const baseData = {
      property: {
        id: propertyId || "",
        name: selectedPropertyName || "",
      },
      unit: {
        id: formData.selectedUnit?.value || "",
        unitNo: formData.selectedUnit?.label || "N/A",
      },
      createdById: userUid || "",
      loan: Number(formData.selectedPercentage) || 0,
    };

    const commonCharges = {
      perferredLocationCharge:
        Number(unitInfo?.preferredLocationCharge) || null,
      gst: Number(unitInfo?.gst) || null,
      registrationCharge: Number(unitInfo?.registrationCharge) || null,
      documentCharge: Number(unitInfo?.documentCharge) || null,
    };

    switch (selectedPropertyType) {
      case "Layout":
        return {
          ...baseData,
          overAllSqft: Number(unitInfo?.sqft) || null,
          pricePerSqft: Number(unitInfo?.ratePerSqft) || null,
          unitFacingSide: unitInfo?.roadFacingSide?.name || "N/A",

          surveyFee: Number(unitInfo?.surveyFee) || null,
          ...commonCharges,
        };

      case "Villa":
        return {
          ...baseData,
          saleableArea: Number(unitInfo?.saleableArea) || null,
          baseRate: Number(unitInfo?.baseRate) || null,
          totalFloor: Number(unitInfo?.totalFloor) || null,
          totalBhk: Number(unitInfo?.totalBhk) || null,
          unitFacingSide: unitInfo?.roadFacingSide?.name || "N/A",

          fullyCoveredParkingCharge:
            Number(
              unitInfo?.fullyCoveredParkingCharge *
                formData?.parkingOptions.fullyCovered
            ) || null,
          semiCoveredParkingCharge:
            Number(
              unitInfo?.semiCoveredParkingCharge *
                formData?.parkingOptions.semiCovered
            ) || null,
          openParkingCharge:
            Number(
              unitInfo?.openParkingCharge * formData?.parkingOptions.open
            ) || null,
          fullyCoveredParkingCount:
            Number(formData?.parkingOptions.fullyCovered) || null,
          semiCoveredParkingCount:
            Number(formData?.parkingOptions.semiCovered) || null,
          openParkingCount: Number(formData?.parkingOptions.open) || null,
          electricityCharge: Number(unitInfo?.electricityCharge) || null,
          furnishedCharge: Number(unitInfo?.furnishedCharge) || null,
          furnishedType: formData.selectedFurnished || "",
          surveyFee: Number(unitInfo?.surveyFee) || null,
          ...commonCharges,
        };

      case "Apartment":
        return {
          ...baseData,
          tower: {
            id: selectedTower?.value || "",
            name: selectedTower?.label || "",
          },
          unitFloor: Number(unitInfo?.unitFloor) || null,
          saleableArea: Number(unitInfo?.saleableArea) || null,
          baseRate: Number(unitInfo?.baseRate) || null,
          totalBhk: Number(unitInfo?.totalBhk) || null,
          unitFacingSide: unitInfo?.unitFacingSide || "",
          uds: Number(unitInfo?.uds) || null,
          fullyCoveredParkingCharge:
            Number(
              unitInfo?.fullyCoveredParkingCharge *
                formData?.parkingOptions.fullyCovered
            ) || null,
          semiCoveredParkingCharge:
            Number(
              unitInfo?.semiCoveredParkingCharge *
                formData?.parkingOptions.semiCovered
            ) || null,
          openParkingCharge:
            Number(
              unitInfo?.openParkingCharge * formData?.parkingOptions.open
            ) || null,
          fullyCoveredParkingCount:
            Number(formData?.parkingOptions.fullyCovered) || null,
          semiCoveredParkingCount:
            Number(formData?.parkingOptions.semiCovered) || null,
          openParkingCount: Number(formData?.parkingOptions.open) || null,
          electricityCharge: Number(unitInfo?.electricityCharge) || null,
          furnishedCharge: Number(unitInfo?.furnishedCharge) || null,
          furnishedType: formData.selectedFurnished || "",
          ...commonCharges,
        };

      default:
        return baseData;
    }
  };

  const calculateTotalPrice = () => {
    let basePrice = 0;
    if (selectedPropertyType === "Layout") {
      basePrice =
        parseFloat(unitInfo?.ratePerSqft || 0) *
        parseFloat(unitInfo?.sqft || 0);
    } else {
      basePrice =
        parseFloat(unitInfo?.baseRate || 0) *
        parseFloat(unitInfo?.saleableArea || 0);
    }

    const charges = {
      preferredLocation: parseFloat(unitInfo?.preferredLocationCharge || 0),
      fullyCoveredParking:
        parseFloat(unitInfo?.fullyCoveredParkingCharge || 0) *
        formData.parkingOptions.fullyCovered,
      semiCoveredParking:
        parseFloat(unitInfo?.semiCoveredParkingCharge || 0) *
        formData.parkingOptions.semiCovered,
      openParking:
        parseFloat(unitInfo?.openParkingCharge || 0) *
        formData.parkingOptions.open,
      electricity: parseFloat(unitInfo?.electricityCharge || 0),

      furnished: parseFloat(unitInfo?.furnishedCharge || 0),
   
      document: parseFloat(unitInfo?.documentCharge || 0),
      surveyFee: parseFloat(unitInfo?.surveyFee || 0),
    };

    const totalCharges = Object.values(charges).reduce(
      (sum, charge) => sum + charge,
      0
    );
    const subtotal = basePrice + totalCharges ;
    const gstAmount = Number(
      subtotal * (parseFloat(unitInfo?.gst || 0) / 100)
    );

    const registionTotal =  parseFloat(unitInfo?.registrationCharge || 0)

    console.log("gstAmount ", gstAmount)
    console.log("Subtotal" , subtotal)

    return (subtotal + gstAmount + registionTotal).toFixed(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div
        className={`bg-white p-6 rounded-lg shadow-lg ${
          selectedPropertyType !== "Layout" && currentStep === 3
            ? "w-[900px]"
            : "w-[650px]"
        } h-[754px] relative`}
      >
        <div className="flex bg-slate-100 p-2 rounded-md justify-between items-center mb-6">
          <h2 className="text-lg font-medium text-green-700">
            <div className="cursor-pointer flex items-center justify-center gap-2 text-base text-customGreen font-medium w-full rounded-[10px] p-2">
              {selectedPropertyName || "None"} ({selectedPropertyType}) :
              Estimate Generation
            </div>
          </h2>
          <button
            onClick={handleClose}
            className="text-red-600 bg-white px-2 focus:outline-none rounded-full p-1"
            aria-label="Close modal"
          >
            <svg
              className="w-5 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <CustomStepProgress
          steps={steps}
          currentStep={currentStep}
          onStepClick={(step) => {
            if (step < currentStep) setCurrentStep(step);
          }}
          className="my-6"
          showClose={true}
          onClose={handleClose}
        />

        <div className="mt-8 overflow-x-auto h-[550px] ">
          {currentStep === 1 && (
            <BasicInfoStep
              selectedPropertyType={selectedPropertyType}
              towerOptions={towerOptions}
              setSearchLead={setSearchLead}
              searchLead={searchLead}
              unitNumberOptions={unitNumberOptions}
              leadOptions={leadOptions}
              formData={formData}
              setSelectedTower={setSelectedTower}
              selectedTower={selectedTower}
              onInputChange={handleInputChange}
              onSubmit={handleBasicInfoSubmit}
              loading={loading}
              errors={validationErrors}
            />
          )}

          {currentStep === 2 && (
            <PropertyInfoStep
              selectedPropertyType={selectedPropertyType}
              unitInfo={unitInfo}
              formData={formData}
              onInputChange={handleInputChange}
              onParkingChange={handleParkingChange}
              onGenerate={handleGenerateEstimate}
              errors={validationErrors}
            />
          )}

          {currentStep === 3 && (
            <ChargesStep
              selectedPropertyType={selectedPropertyType}
              unitInfo={unitInfo}
              formData={formData}
              parkingOptions={formData.parkingOptions}
              onInputChange={handleUnitInfoChange}
              onDownload={handleDownloadEstimate}
              totalPrice={calculateTotalPrice()}
              loading={loading}
            />
          )}
        </div>
      </div>
      {/* <EstimateDownloadPDF ref={pdfRef} /> */}

      {generatingPDF && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white px-8 py-5 rounded shadow flex flex-col items-center">
            <img
              src={loadingGIF}
              alt="Generating PDF..."
              className="w-36 h-36 mb-3"
            />
            <p className="text-green-600 font-medium text-sm">
              Generating Estimate PDF…
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateGeneration;
